import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { LOGO_URL } from "../lib/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/about", label: "About" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled || open
          ? "bg-white/80 backdrop-blur-xl border-ink/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Inubaan Labs"
            className="w-20 h-20 object-contain transition-transform duration-500 group-hover:rotate-[15deg]"
          />
          <span className="font-display font-bold text-base md:text-lg tracking-tight text-ink">
            INUBAAN<span className="text-magenta">.</span>LABS
          </span>
        </Link>

        <nav data-testid="desktop-nav" className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:text-cyan ${
                  isActive ? "text-cyan" : "text-slate-600"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className="group flex items-center gap-2 bg-ink text-white font-semibold text-sm rounded-full pl-5 pr-4 py-2.5 transition-colors duration-300 hover:bg-magenta hover:text-white"
          >
            Start a project
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-ink p-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-ink/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                  className={({ isActive }) =>
                    `font-display text-3xl font-bold tracking-tight ${isActive ? "text-magenta" : "text-ink"}`
                  }
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-button"
                className="mt-2 inline-flex w-fit items-center gap-2 bg-magenta text-white font-semibold rounded-full px-6 py-3"
              >
                Start a project <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
